import { createClient } from '@supabase/supabase-js';
import type { Database } from '../types/supabase';

// Extend the Window interface to include ENV
declare global {
  interface Window {
    ENV?: {
      VITE_SUPABASE_URL?: string;
      VITE_SUPABASE_ANON_KEY?: string;
    };
  }
}

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    storageKey: 'supabase.auth.token',
    storage: typeof window !== 'undefined' ? localStorage : undefined,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    debug: false
  },
  global: {
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Client-Info': 'product-widget-app'
    }
  }
});

// Add connection check function with better error handling
async function checkSupabaseConnection() {
  try {
    const { data, error } = await supabase.from('profiles').select('count').single();
    
    if (error) {
      return false;
    }
    
    return true;
  } catch (error) {
    return false;
  }
}

// Export the check function
export const checkConnection = checkSupabaseConnection;

// Add a function to get the current user's profile
export async function getCurrentUserProfile() {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    
    if (authError) {
      return null;
    }
    
    if (!user) {
      return null;
    }

    const { data: profile, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();

    if (error) {
      return null;
    }

    return profile;
  } catch (error) {
    return null;
  }
}

// Fonction d'aide pour effectuer des requêtes Supabase
// Cette fonction ajoute automatiquement les en-têtes appropriés
export async function fetchWithSupabase<T>(
  tableName: string,
  method: 'select' | 'insert' | 'update' | 'delete',
  options: {
    select?: string;
    match?: Record<string, any>;
    data?: Record<string, any>;
    single?: boolean;
  } = {}
): Promise<{ data: T | null; error: any }> {
  try {
    // Configurer les en-têtes appropriés
    const fetchOptions = {
      method: method === 'select' ? 'GET' : 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'apikey': supabaseAnonKey,
        'Authorization': `Bearer ${supabaseAnonKey}`
      },
      body: method !== 'select' && options.data ? JSON.stringify(options.data) : undefined
    };

    // Construire l'URL
    let url = `${supabaseUrl}/rest/v1/${tableName}`;

    // Ajouter les paramètres pour SELECT
    if (method === 'select') {
      const params = new URLSearchParams();
      if (options.select) {
        params.append('select', options.select);
      }
      if (options.match) {
        for (const [key, value] of Object.entries(options.match)) {
          params.append(`${key}`, `eq.${value}`);
        }
      }
      if (options.single) {
        params.append('limit', '1');
      }
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
    }

    // Effectuer la requête
    const response = await fetch(url, fetchOptions);
    
    if (!response.ok) {
      return { data: null, error: { message: `HTTP error ${response.status}` } };
    }
    
    const data = await response.json();
    return { data: options.single ? data[0] || null : data, error: null };
  } catch (error) {
    return { data: null, error };
  }
}